import React, { useRef, useMemo } from 'react';
import './index.scss';
import { useColumnsFilter, useEntityContext } from '~/hooks';
import { ChevronDown } from 'react-feather';
import { Popover } from 'react-tiny-popover';
import Pill from '../Pill';
import TagsPicker from '../TagsPicker';
import { TagsContext } from '../../../context/tags';
import { PillColumnFilterInterface } from '~/components/UIElements/PillColumnFilter';
import { TagsProviderInterface } from '~/modules/Tags/types/TagsContext.interface';
import { ColumnValueOption } from '~/interfaces/columnValues/ColumnFilterContext.interface';

const PillColumnFilter: React.FC<PillColumnFilterInterface> = ({
    prefix,
    intl,
    columnName,
    resetFilterColumn,
    columnFilters,
    onApplyFilter,
    columnValues = [],
    sortParams = {},
    sortFieldName,
    placeholder,
    disabled,
}) => {
    const menuRef = useRef(null);
    const { transformedFolderStructure } =
        useEntityContext<TagsProviderInterface>(TagsContext);

    const {
        isFiltered,
        sortFilter,
        selectedValues,
        filteredValues,
        onResetFilter,
        filterMenu,
    } = useColumnsFilter({
        columnName,
        resetFilterColumn,
        columnFilters,
        onApplyFilter,
        columnValues,
        sortParams,
        sortFieldName,
        isDynamicColumnFilter: false,
    });

    const selectedTags = useMemo(() => {
        if (!isFiltered) return [];
        return columnValues.filter((t) =>
            selectedValues.some((val) => val.value === t.value)
        );
    }, [selectedValues, isFiltered]);
    const onApply = (tags: ColumnValueOption[]) => {
        if (!tags.length) {
            onResetFilter();
            filterMenu.setShowMenu(false);
            return;
        }

        onApplyFilter({
            sortType: sortFilter,
            column: columnName,
            values: tags,
            sortBy: sortFieldName,
            isDynamicColumnFilter: false,
        });
        filterMenu.handleMenu();
    };

    const displayValue = useMemo(() => {
        const valuesCount = columnFilters.find(
            (col) => col.column === columnName
        )?.value.length;

        if (!valuesCount) {
            return placeholder;
        }

        return (
            valuesCount +
            ' ' +
            intl.formatMessage({
                id: valuesCount !== 1 ? 'values' : 'value',
            })
        );
    }, [columnFilters]);

    // filter transformed folder structure in order to show only the tags that are present in the column values
    const filteredFolderStructure = useMemo(() => {
        if (!transformedFolderStructure || !columnValues.length) return [];

        const filteredTags = transformedFolderStructure.map((folder) => {
            const tags = folder.items.filter((tag) =>
                columnValues.some((c) => c.value === tag.value)
            );

            return {
                ...folder,
                items: tags,
            };
        });

        return filteredTags;
    }, [transformedFolderStructure, columnValues]);

    return (
        <div ref={menuRef}>
            <Popover
                isOpen={filterMenu.showMenu}
                positions={['bottom', 'left', 'right']}
                align="start"
                content={
                    <div className="tags-column-filter">
                        <TagsPicker
                            intl={intl}
                            tags={filteredValues}
                            selectedTags={selectedTags}
                            onApply={onApply}
                            onCancel={() => {
                                filterMenu.setShowMenu(false);
                                onResetFilter();
                            }}
                            showCancelButton={isFiltered}
                            allowCreation={false}
                            folderStructure={filteredFolderStructure}
                        />
                    </div>
                }
                onClickOutside={() => filterMenu.setShowMenu(false)}
            >
                <div
                    className={`pill-column-filter__header-body ${disabled ? 'pill-column-filter__disabled' : ''}`}
                >
                    <Pill
                        onClick={() => !disabled && filterMenu.handleMenu()}
                        clickable
                    >
                        <div className="pill-column-filter__content">
                            {prefix && (
                                <span className="pill-column-filter__prefix">
                                    {prefix}:{' '}
                                </span>
                            )}
                            <span className="pill-column-filter__placeholder">
                                {displayValue}
                            </span>
                        </div>
                        <ChevronDown />
                    </Pill>
                </div>
            </Popover>
        </div>
    );
};

export default PillColumnFilter;
